import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Download, ArrowLeft, Sparkles, ShieldCheck, Mail } from "lucide-react";

const LOGO = "/assets/logo.png";
const EBOOK_COVER = "/assets/free-ebook-cover.webp";
const EBOOK_FILE = "/ebooks/how-to-make-money-on-social-media.pdf";

export default function Subscribers() {
  const [downloaded, setDownloaded] = useState(false);

  const handleDownload = () => {
    setDownloaded(true);
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "file_download", { file_name: "how-to-make-money-on-social-media" });
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white grain relative overflow-hidden">
      {/* Ambient gold glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-[#D4AF37]/8 blur-[140px] rounded-full pointer-events-none" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 py-12 md:py-16">
        {/* Header */}
        <div className="flex items-center gap-3 mb-14">
          <img src={LOGO} alt="Social Media Combo logo" className="w-9 h-9 object-contain" />
          <span className="font-serif-display text-lg tracking-[0.25em] font-semibold">
            SOCIAL MEDIA COMBO<span className="text-[#D4AF37]">.</span>
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left — message */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="inline-flex items-center gap-2 border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-5 py-2 mb-8">
              <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
                Subscription confirmed
              </span>
            </div>

            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]">
              Thank You for <span className="gold-shimmer">Subscribing</span>
            </h1>

            <p className="mt-6 text-lg text-zinc-400 leading-relaxed max-w-xl">
              Welcome to the inside circle! Your free ebook —{" "}
              <span className="text-white font-semibold">
                "How to Make Money on Social Media Without a Large Following"
              </span>{" "}
              — is ready. Click below to download your copy and start building your faceless empire today.
            </p>

            <div className="mt-10">
              <a
                href={EBOOK_FILE}
                download
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleDownload}
                data-testid="ebook-download-button"
                className="btn-gold inline-flex items-center justify-center gap-3 px-10 py-5 text-base sm:text-lg"
              >
                <Download className="w-5 h-5" />
                {downloaded ? "Download Again" : "Download Your Free Ebook"}
              </a>
            </div>

            {downloaded && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                data-testid="download-confirm"
                className="mt-5 flex items-center gap-2 text-sm text-[#D4AF37]"
              >
                <CheckCircle2 className="w-4 h-4" /> Your download has started. Enjoy!
              </motion.p>
            )}

            <div className="mt-10 flex flex-col gap-3 text-sm text-zinc-500">
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[#D4AF37]" /> A copy has also been sent to your inbox.
              </span>
              <span className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#D4AF37]" /> No spam — unsubscribe anytime.
              </span>
            </div>

            <Link
              to="/"
              data-testid="back-to-store-link"
              className="inline-flex items-center gap-2 mt-12 text-sm text-zinc-400 hover:text-[#D4AF37] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Explore the full bundle
            </Link>
          </motion.div>

          {/* Right — ebook cover */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="absolute -inset-8 bg-[#D4AF37]/15 blur-3xl rounded-full" />
            <div className="relative z-10 flex items-center justify-center">
              <div className="relative">
                <span className="absolute -top-3 -right-3 z-20 flex items-center gap-1.5 bg-[#D4AF37] text-black text-xs font-extrabold uppercase tracking-widest px-3 py-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Free Gift
                </span>
                <img
                  src={EBOOK_COVER}
                  alt="How to Make Money on Social Media Without a Large Following — free ebook by Social Media Combo for faceless digital entrepreneurs"
                  data-testid="ebook-cover-image"
                  className="w-full max-w-sm mx-auto border border-[#D4AF37]/30 drop-shadow-[0_20px_60px_rgba(0,0,0,0.8)]"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
